/**
 * Pure form mapping and validation for normalized sensor configuration.
 *
 * Contains:
 * - initial-value and blade-tab builders
 * - contract-aware validation
 * - sparse PATCH generation and error-path mapping
 *
 * Returns plain JavaScript and performs no JSX rendering or network I/O.
 */
import { t } from 'i18next';
import * as yup from 'yup';

import { cToRaw, rhToRaw } from '@sophocles/utils/sensorConversions';
import buildRenderedPatchKnownFields from '@sophocles/views/sites/components/SiteDashboard/configPatchErrorFields';

const SENSOR_TYPE_COLOR = 1;
const SENSOR_TYPE_TEMP_HUMIDITY = 2;
const NESTED_SENSOR_FIELDS = new Set(['location', 'temperature', 'humidity']);
const COMMON_FIELDS = [
  'green_tolerance',
  'red_tolerance',
  'blue_tolerance',
  'watermark_check_time',
  'stuck_check_count',
  'stuck_margin',
];
export const SENSOR_CONFIG_SCHEMA_VERSION = 1;

/**
 * @param {Array} sensors Formik sensor rows
 * @returns {Array<{key: string, kind: 'common'|'sensor', sensorIndex?: number}>}
 */
export function buildSensorBladeTabs(sensors) {
  const sensorTabs = sensors.map((sensor, sensorIndex) => {
    const identity = sensor.key ?? `${sensor.sensor_type}:${sensor.sensor_number ?? sensorIndex}`;
    return { key: `sensor:${identity}`, kind: 'sensor', sensorIndex };
  });

  return [{ key: 'common', kind: 'common' }, ...sensorTabs];
}

/** Build a temperature leaf object, defaulting unreported bounds like a physical sensor's. */
function buildTemperatureValue(temperature) {
  return {
    high: typeof temperature?.high === 'number' ? temperature.high : cToRaw(60),
    low: typeof temperature?.low === 'number' ? temperature.low : cToRaw(0),
    // Hysteresis uses a Celsius delta in raw units.
    hysteresis: typeof temperature?.hysteresis === 'number' ? temperature.hysteresis : cToRaw(5),
  };
}

/** Build a humidity leaf object, defaulting unreported bounds like a physical sensor's. */
function buildHumidityValue(humidity) {
  return {
    high: typeof humidity?.high === 'number' ? humidity.high : rhToRaw(0),
    low: typeof humidity?.low === 'number' ? humidity.low : rhToRaw(95),
    hysteresis: typeof humidity?.hysteresis === 'number' ? humidity.hysteresis : rhToRaw(5),
  };
}

/**
 * @param {object|null} common canonical unit-wide sensor configuration
 * @returns {object} Formik-ready Common values and backend metadata
 *
 * Fixed-generation firmware nests temperature/humidity bounds directly on
 * Common instead of a dedicated sensor row; those objects are carried
 * through (with the same physical-sensor-style leaf defaults) whenever the
 * backend document reports them.
 */
export function buildCommonInitialValue(common) {
  const source = common && typeof common === 'object' ? common : {};
  return {
    ...Object.fromEntries(COMMON_FIELDS.filter(field => field in source).map(field => [field, source[field]])),
    ...('temperature' in source ? { temperature: buildTemperatureValue(source.temperature) } : {}),
    ...('humidity' in source ? { humidity: buildHumidityValue(source.humidity) } : {}),
    editable_fields: [...(source.editable_fields ?? [])],
    constraints: { ...(source.constraints ?? {}) },
  };
}

/**
 * Build sorted Formik values with defaults for unreported fields.
 * @param {Array} sensors raw sensor config rows
 * @returns {Array} Formik-ready sensor values, in display order
 */
export function buildSensorInitialValues(sensors) {
  return [...sensors]
    .sort((a, b) => a.sensor_number - b.sensor_number)
    .map(sensor => {
      const editableFields = sensor.editable_fields;
      const includeRedBlue =
        'red_threshold' in sensor ||
        'blue_threshold' in sensor ||
        sensor.editable_fields?.includes('red_threshold') ||
        sensor.editable_fields?.includes('blue_threshold');

      return {
        ...(sensor.key != null ? { key: sensor.key } : {}),
        sensor_number: sensor.sensor_number,
        sensor_type: sensor.sensor_type,
        ...(editableFields != null ? { editable_fields: editableFields } : {}),
        constraints: { ...(sensor.constraints ?? {}) },
        name: sensor.name ?? t('sites.sensorNumber', 'Sensor {{number}}', { number: sensor.sensor_number + 1 }),
        location: sensor.sensor_type === SENSOR_TYPE_COLOR ? sensor.location || { x: 0, y: 0 } : undefined,
        size: sensor.sensor_type === SENSOR_TYPE_COLOR ? sensor.size || 32 : undefined,
        threshold: sensor.sensor_type === SENSOR_TYPE_COLOR ? (sensor.threshold ?? 1500) : undefined,
        red_threshold:
          sensor.sensor_type === SENSOR_TYPE_COLOR && includeRedBlue ? (sensor.red_threshold ?? 1500) : undefined,
        blue_threshold:
          sensor.sensor_type === SENSOR_TYPE_COLOR && includeRedBlue ? (sensor.blue_threshold ?? 1500) : undefined,
        temperature:
          sensor.sensor_type === SENSOR_TYPE_TEMP_HUMIDITY ? buildTemperatureValue(sensor.temperature) : undefined,
        humidity: sensor.sensor_type === SENSOR_TYPE_TEMP_HUMIDITY ? buildHumidityValue(sensor.humidity) : undefined,
      };
    });
}

/** Build one required integer leaf from backend-provided scalar constraints. */
function numericSchema(configuration, path, messages) {
  const constraint = configuration?.constraints?.[path] ?? {};
  let schema = yup.number().typeError(messages.required).integer(messages.wholeNumber).required(messages.required);
  if (constraint.min != null) schema = schema.min(constraint.min, messages.min(constraint.min));
  if (constraint.max != null) schema = schema.max(constraint.max, messages.max(constraint.max));
  return schema;
}

function configurationSchema(configuration, messages) {
  const editable = new Set(configuration?.editable_fields ?? []);
  const shape = {};

  if (editable.has('name')) {
    const maxLength = configuration?.constraints?.name?.max_length;
    shape.name = maxLength == null ? yup.string() : yup.string().max(maxLength, messages.maxCharacters(maxLength));
  }
  for (const field of COMMON_FIELDS) {
    if (editable.has(field)) shape[field] = numericSchema(configuration, field, messages);
  }
  for (const field of ['size', 'threshold', 'red_threshold', 'blue_threshold']) {
    if (editable.has(field)) shape[field] = numericSchema(configuration, field, messages);
  }
  for (const field of NESTED_SENSOR_FIELDS) {
    if (!editable.has(field)) continue;
    const leaves = field === 'location' ? ['x', 'y'] : ['high', 'low', 'hysteresis'];
    shape[field] = yup.object(
      Object.fromEntries(leaves.map(leaf => [leaf, numericSchema(configuration, `${field}.${leaf}`, messages)])),
    );
  }
  return yup.object(shape);
}

/**
 * @param {object} messages - translated validation messages; `required` and
 *   `wholeNumber` are strings, `min`/`max`/`maxCharacters` are `value => string`
 *   builders for the bound-interpolated messages.
 * @returns {yup.ObjectSchema} schema for the canonical Formik document.
 */
export function buildSensorValidationSchema(messages) {
  return yup.object({
    common: yup.lazy(configuration => configurationSchema(configuration, messages)),
    sensors: yup.array().of(yup.lazy(configuration => configurationSchema(configuration, messages))),
  });
}

function valuesMatch(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function changedObjectLeaves(initialValue, currentValue) {
  return Object.entries(currentValue ?? {}).reduce(
    (changes, [field, value]) => (valuesMatch(initialValue?.[field], value) ? changes : { ...changes, [field]: value }),
    {},
  );
}

/**
 * Diff one editable-fields document (a sensor row or Common) against its
 * initial values. Nested fields (temperature/humidity/location, on either
 * document) patch only their changed leaves; scalar fields patch whole.
 */
function diffEditableFields(initial, current, editableFields) {
  const patch = {};
  (editableFields ?? []).forEach(field => {
    if (NESTED_SENSOR_FIELDS.has(field)) {
      const leaves = changedObjectLeaves(initial?.[field], current?.[field]);
      if (Object.keys(leaves).length > 0) patch[field] = leaves;
      return;
    }
    if (!valuesMatch(initial?.[field], current?.[field])) patch[field] = current[field];
  });
  return patch;
}

/**
 * Build a minimal schema-versioned management PATCH. Only fields whose values
 * changed are included, preventing an unrelated name edit from overwriting
 * fresher device configuration or dispatching unnecessary hardware writes.
 */
export function generateSensorConfigPatch(initialValues, currentValues) {
  const initialByKey = new Map((initialValues.sensors ?? []).map(sensor => [sensor.key, sensor]));
  const sensorPatches = (currentValues.sensors ?? []).flatMap(sensor => {
    const initial = initialByKey.get(sensor.key);
    if (!initial) return [];

    const fieldPatch = diffEditableFields(initial, sensor, sensor.editable_fields);
    return Object.keys(fieldPatch).length > 0 ? [{ key: sensor.key, ...fieldPatch }] : [];
  });

  const commonPatch = diffEditableFields(
    initialValues.common,
    currentValues.common,
    currentValues.common?.editable_fields,
  );

  return {
    schema_version: initialValues.schema_version,
    ...(sensorPatches.length > 0 ? { sensors: sensorPatches } : {}),
    ...(Object.keys(commonPatch).length > 0 ? { common: commonPatch } : {}),
  };
}

/**
 * @param {object} patch sparse sensor management PATCH
 * @returns {string[]} API paths backed by rendered form controls
 */
export function buildSensorPatchKnownFields(patch) {
  return buildRenderedPatchKnownFields(patch);
}

/**
 * @param {string} path backend validation path
 * @param {object} patch sparse generated management PATCH
 * @param {{sensors: object[]}} values current Formik values
 * @returns {string} matching full Formik field path
 */
export function mapSensorPatchErrorPath(path, patch, values) {
  const match = path.match(/^sensors\.(\d+)(\..+)?$/);
  if (!match) return path;
  const patchSensor = patch.sensors?.[Number(match[1])];
  const formIndex = values.sensors.findIndex(sensor => sensor.key === patchSensor?.key);
  return formIndex >= 0 ? `sensors.${formIndex}${match[2] ?? ''}` : path;
}
